import { type OperationVariables, type LazyQueryResultTuple, NetworkStatus } from '@apollo/client'
import { useDeepMemo } from '@retailer-platform/shared-common'
import {
  type BaseErrorResponse,
  type BasePendingResponse,
  type BaseSuccessResponse,
} from './baseResponse'

export type BaseLazyResponse<TVariables> = {
  execute: (options?: { variables?: TVariables }) => void
  networkStatus: NetworkStatus
}

export type PendingLazyResponse<TVariables> = BaseLazyResponse<TVariables> & BasePendingResponse
export type SuccessLazyResponse<TData, TVariables> = BaseLazyResponse<TVariables> &
  BaseSuccessResponse<TData>
export type ErrorLazyResponse<TVariables> = BaseLazyResponse<TVariables> & BaseErrorResponse
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type LazyResponse<TData = unknown, TVariables = any> =
  | PendingLazyResponse<TVariables>
  | SuccessLazyResponse<TData, TVariables>
  | ErrorLazyResponse<TVariables>

export function useApolloRequestLazyWrapper<
  TResult,
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables
>({
  resultTuple,
  mapResponse,
}: {
  resultTuple: LazyQueryResultTuple<TData, TVariables>
  mapResponse: (data: TData) => TResult
}): LazyResponse<TResult, TVariables> {
  return useDeepMemo(() => {
    const [execute, query] = resultTuple
    const { data, error, loading: isLoading, networkStatus } = query
    const baseResponse: BaseLazyResponse<TVariables> = {
      execute: (options?: { variables?: TVariables }) => execute({ variables: options?.variables }),
      networkStatus,
    }

    if (error) {
      return {
        type: 'error',
        isLoading: false,
        error,
        ...baseResponse,
      }
    }
    if (networkStatus === NetworkStatus.ready && !data) {
      return {
        type: 'pending',
        isLoading: false,
        ...baseResponse,
      }
    }
    if (isLoading) {
      return {
        type: 'pending',
        isLoading: true,
        ...baseResponse,
      }
    }

    const parsedData = mapResponse(data!)
    return {
      type: 'success',
      data: parsedData,
      isLoading: false,
      ...baseResponse,
    }
  }, [resultTuple, mapResponse])
}
